import { Row } from "@tanstack/react-table";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { IProductAssignment } from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

type TUseToolsReturnTableColumns = {
  getActionOptions: (row: Row<IProductAssignment>) => ActionButtonDataTypes[];
};
export const useToolsReturnTableColumns = ({
  getActionOptions,
}: TUseToolsReturnTableColumns): ExtendedColumnDef<IProductAssignment>[] => {
  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.product?.images?.[0]}
            name={`${row.original.product?.name}`}
            title={row.original.product?.name ?? ""}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        showRightBorder: true,
        className: "max-w-[120px]",
        classNameRow: "max-w-[120px]",
      },
    },
    {
      map: {
        key: "Sub Products",
      },
      accessorKey: "Sub Products",
      header: "Sub Products",
      cell: ({ row }) =>
        row.original?.subProductAssignments.length
          ? row.original?.subProductAssignments
              ?.map((subProduct) => subProduct.subProduct.name)
              .join(", ")
          : "---",
      meta: {
        showRightBorder: true,
        classNameRow: "max-w-[100px]",
      },
    },
    {
      map: {
        key: "Quantity",
      },
      accessorKey: "Quantity",
      header: "Quantity",
      cell: ({ row }) =>
        row.original?.assignedQuantity ||
        row.original.subProductAssignments.length ||
        "-",
      meta: {
        showRightBorder: true,
        className: "min-w-[30px]",
      },
    },
    {
      map: {
        key: "Notes",
      },
      accessorKey: "Notes",
      header: "Notes",
      cell: () => "Notes",
      meta: {
        className: "max-w-[70px]",
        classNameRow: "max-w-[70px]",
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "ReturnedAt",
      },
      accessorKey: "ReturnedAt",
      header: "ReturnedAt",
      cell: ({ row }) => dateFormat(row.original.updatedAt, "dd/MM/yyyy") || "",
      meta: {
        className: "max-w-[70px]",
        classNameRow: "max-w-[70px]",
      },
    },

    {
      id: "actions",
      cell: ({ row }: { row: Row<IProductAssignment> }): React.JSX.Element => {
        return (
          <>
            <CustomDropdown data={getActionOptions(row)} />
          </>
        );
      },
      meta: {
        classNameRow: "w-20",
      },
    },
  ];
};
